/**
 * NetworkStatus - Shows online/offline indicator
 */

import { useState, useEffect } from 'react'
import { Wifi, WifiOff } from 'lucide-react'
import clsx from 'clsx'
import { useTranslation } from '@/i18n'

export function NetworkStatus() {
  const { t } = useTranslation()
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  )
  const [showOnlineToast, setShowOnlineToast] = useState(false)

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
      // Show "back online" toast briefly
      setShowOnlineToast(true)
      setTimeout(() => setShowOnlineToast(false), 3000)
    }

    const handleOffline = () => {
      setIsOnline(false)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Only show when offline or briefly when coming back online
  if (isOnline && !showOnlineToast) {
    return null
  }

  return (
    <div
      className={clsx(
        'fixed bottom-4 left-1/2 -translate-x-1/2 z-50',
        'flex items-center gap-2 px-4 py-2 rounded-full shadow-lg',
        'animate-in slide-in-from-bottom-4 duration-300',
        isOnline
          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800'
          : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
      )}
    >
      {isOnline ? (
        <>
          <Wifi className="h-4 w-4" />
          <span className="text-sm font-medium">{t('chatv2:network.online', { defaultValue: 'Back online' })}</span>
        </>
      ) : (
        <>
          <WifiOff className="h-4 w-4" />
          <span className="text-sm font-medium">{t('chatv2:network.offline', { defaultValue: 'You\'re offline' })}</span>
        </>
      )}
    </div>
  )
}

export default NetworkStatus
